import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LivenessWidget from '../components/LivenessWidget';
import { api } from '../api/client';
import ShieldIcon from '../components/ShieldIcon';
import { brand } from '../config/brand';

type Phase = 'loading' | 'prep' | 'scanning' | 'completing' | 'error';

export default function CandidateCheck() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [consented, setConsented] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setErrorMsg('Invalid session link.');
      setPhase('error');
      return;
    }

    api
      .getSession(sessionId)
      .then((session) => {
        if (session.status !== 'PENDING') {
          setErrorMsg('This liveness check has already been completed or has expired.');
          setPhase('error');
        } else {
          setPhase('prep');
        }
      })
      .catch(() => {
        setErrorMsg('Session not found. This link may be invalid or expired.');
        setPhase('error');
      });
  }, [sessionId]);

  async function handleComplete() {
    if (!sessionId) return;
    setPhase('completing');
    try {
      await api.completeSession(sessionId);
      navigate('/result', { replace: true });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
      setPhase('error');
    }
  }

  async function handleBegin() {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
      setPhase('scanning');
    } catch (err) {
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setCameraError(
            'Camera access is required for verification. Please allow camera access in your browser settings and try again.',
          );
          return;
        }
        if (err.name === 'NotFoundError' || err.name === 'NotReadableError') {
          setCameraError('No camera detected. Please connect a camera and try again.');
          return;
        }
      }
      setCameraError('Unable to access camera. Please check your device and try again.');
    }
  }

  function handleError(err: Error) {
    console.error('Liveness check error', err);
    setErrorMsg('The liveness check encountered an error. Please refresh and try again.');
    setPhase('error');
  }

  const stepNum = phase === 'prep' ? 1 : phase === 'scanning' ? 2 : phase === 'completing' ? 3 : 1;

  return (
    <div className="check-page">
      <div className="check-topbar">
        <ShieldIcon size={22} />
        {brand.name}
      </div>

      {phase !== 'error' && (
        <div className="check-steps">
          <div className={`check-step ${stepNum >= 1 ? (stepNum > 1 ? 'done' : 'active') : ''}`}>
            <span className="check-step-dot">{stepNum > 1 ? '✓' : '1'}</span>
            Prepare
          </div>
          <div className={`check-step-line ${stepNum > 1 ? 'done' : ''}`} />
          <div className={`check-step ${stepNum >= 2 ? (stepNum > 2 ? 'done' : 'active') : ''}`}>
            <span className="check-step-dot">{stepNum > 2 ? '✓' : '2'}</span>
            Scan
          </div>
          <div className={`check-step-line ${stepNum > 2 ? 'done' : ''}`} />
          <div className={`check-step ${stepNum >= 3 ? 'active' : ''}`}>
            <span className="check-step-dot">3</span>
            Complete
          </div>
        </div>
      )}

      {phase === 'loading' && (
        <div className="check-processing">
          <div className="spinner-ring" />
          <p>Loading your check...</p>
        </div>
      )}

      {phase === 'prep' && (
        <div className="check-prep">
          <h1>Verify Your Identity</h1>
          <p className="check-prep-desc">
            This takes about 15 seconds. You'll need to position your face within an oval and follow
            the on-screen instructions.
          </p>
          <div className="check-trust-badges">
            <span className="check-trust-badge">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              256-bit encrypted
            </span>
            <span className="check-trust-badge">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              No biometric data stored
            </span>
            <span className="check-trust-badge">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              Powered by AWS Rekognition
            </span>
          </div>
          <label className="check-consent-checkbox">
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
            />
            <span>I consent to a biometric liveness check</span>
          </label>
          {cameraError && <p className="check-camera-error">{cameraError}</p>}
          <button
            className="btn btn-primary check-begin-btn"
            onClick={handleBegin}
            disabled={!consented}
          >
            Begin Verification
          </button>
        </div>
      )}

      {phase === 'scanning' && sessionId && (
        <div className="check-widget">
          <LivenessWidget
            sessionId={sessionId}
            onComplete={() => void handleComplete()}
            onError={handleError}
          />
        </div>
      )}

      {phase === 'completing' && (
        <div className="check-processing">
          <div className="spinner-ring" />
          <p>Verifying your identity...</p>
        </div>
      )}

      {phase === 'error' && (
        <div className="check-error">
          <div className="check-error-card">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ margin: '0 auto' }}
            >
              <circle cx="12" cy="12" r="10" />
              <path d="m15 9-6 6M9 9l6 6" />
            </svg>
            <h2>Unable to complete check</h2>
            <p>{errorMsg}</p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
